import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BUILD_NETWORK, Network } from "../utils/network";
import { UserProfile } from "auth";

interface userState {
  network: Network | undefined;
  // The Firebase uid this tab's stored identity belongs to. Written ONLY by a
  // sign-in performed in this tab, never by a cross-tab sync — that asymmetry is
  // the whole point. Firebase's user is origin-shared, so when another tab signs
  // someone else in, this tab's Firebase user changes while authUid does not, and
  // the mismatch is detectable. Without it, `userType` says nothing about WHICH
  // account.
  authUid: string | undefined;
  userCardNumber: string | undefined;
  userProfile: UserProfile | undefined;
  cardInfo: any;
  cardBalance: number | undefined;
  cardTransactions: any[] | undefined;
  userType: string | undefined;
  showLoadMore: boolean;
  merchantsMap: Record<string, string>;
  /** Raw preference; the effective token is derived in features/tokens/useSettlementToken. */
  settlementTokenAddress: string | undefined;
}

const initialState: userState = {
  network: BUILD_NETWORK,
  authUid: undefined,
  userCardNumber: undefined,
  userProfile: undefined,
  cardInfo: undefined,
  cardBalance: undefined,
  cardTransactions: undefined,
  userType: undefined,
  showLoadMore: true,
  merchantsMap: {},
  settlementTokenAddress: undefined,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthUid: (state, action: PayloadAction<string | undefined>) => {
      state.authUid = action.payload;
    },
    setCardNumber: (state, action: PayloadAction<string>) => {
      state.userCardNumber = action.payload;
    },
    setUserProfile: (state, action: PayloadAction<UserProfile>) => {
      state.userProfile = action.payload;
    },
    setCardInfo: (state, action: PayloadAction<any>) => {
      state.cardInfo = action.payload;
    },
    setCardBalance: (state, action: PayloadAction<any>) => {
      state.cardBalance = action.payload;
    },
    updateCardInfo: (
      state,
      action: PayloadAction<Partial<typeof state.cardInfo>>,
    ) => {
      state.cardInfo = {
        ...state.cardInfo,
        ...action.payload,
      };
    },
    setCardTransactions: (state, action: PayloadAction<any[]>) => {
      state.cardTransactions = action.payload;
    },
    setUserType: (state, action: PayloadAction<string | undefined>) => {
      state.userType = action.payload;
    },
    /**
     * Forget the signed-in session. Everything here is redux-persist whitelisted, so without it a
     * sign-out leaves the card, profile and userType in localStorage for the next person to sign
     * in on this browser. `authUid` deliberately survives — the cross-tab identity guards read it,
     * and a null Firebase user already reports "signed-out" on its own.
     */
    resetUserSession: (state) => {
      state.userCardNumber = undefined;
      state.userProfile = undefined;
      state.cardInfo = undefined;
      state.cardBalance = undefined;
      state.cardTransactions = undefined;
      state.userType = undefined;
      state.showLoadMore = true;
    },
    setShowLoadMore: (state, action: PayloadAction<boolean>) => {
      state.showLoadMore = action.payload;
    },
    cacheMerchant: (
      state,
      action: PayloadAction<{ address: string; merchantName: string }>,
    ) => {
      state.merchantsMap[action.payload.address] = action.payload.merchantName;
    },
    setSettlementTokenAddress: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.settlementTokenAddress = action.payload;
    },
  },
});

export const {
  setAuthUid,
  setCardNumber,
  setUserProfile,
  setCardInfo,
  setCardBalance,
  setCardTransactions,
  setUserType,
  resetUserSession,
  setShowLoadMore,
  cacheMerchant,
  setSettlementTokenAddress,
} = userSlice.actions;
export default userSlice.reducer;
